import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Image,
  Text,
  Button,
  Grid,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Tooltip,
  useColorModeValue,
  FormLabel,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Wrap,
  WrapItem,
  Icon,
  FormControl,
} from "@chakra-ui/react";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  HiEye,
  HiHome,
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineDocumentText,
  HiOutlineCode,
  HiOutlineLibrary,
  HiOutlineStatusOnline,
  HiOutlineMenu,
  HiOutlineCog,
  HiOutlineBookOpen,
  HiX,
} from "react-icons/hi";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";

import { useClocks } from "./hooks/useClocks";
import ClockCard from "./components/ClockCard";
import CodexLookupText from "./CodexLookupText";
import CodesetSelect from "./CodesetSelect";
import ServicePanel from "./ServicePanel";
import AccountNameText from "./utils/AccountNameText";
import FileEditorView from "./FileEditorView";

import MyWorkflowList from "./MyWorkflowList.jsx";
import CodifyDefinitionsGrid from "./components/CodifyDefinitionsGrid";

const Poc = () => {
  const { user } = useAuth();
  const [topicsOutput, setTopicsOutput] = useState("");
  const [topicsTable, setTopicsTable] = useState([]);

  const isUserActive = user?.platformUser?.isActive === "true";
  const isUserSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const isUserAdmin = user?.platformUser?.isAdmin === "true";
  const isUserDeveloper = user?.platformUser?.isDeveloper === "true";
  const navigate = useNavigate();

  const handleOpenParty = (party) => {
    navigate(`/party/${party._id}`);
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [productsContracts, setProductsContracts] = useState([]);
  const [productsContractsLoading, setProductsContractsLoading] =
    useState(true);

  const [parties, setParties] = useState([]);
  const [partyLoading, setPartyLoading] = useState(true);

  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);

  const [documentDefinitions, setDocumentDefinitions] = useState([]);
  const [documentDefinitionsLoading, setDocumentDefinitionsLoading] = useState(true);

  const [codifyDefinitions, setCodifyDefinitions] = useState([]);
  const [codifyDefinitionsLoading, setCodifyDefinitionsLoading] = useState(true);

  const handleCodifyDefinitionCopy = (newDefinition) => {
    setCodifyDefinitions((prev) => [...prev, newDefinition]);
  };

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);

  const [smartLedgers, setSmartLedgers] = useState([]);
  const [smartLedgerLoading, setSmartLedgerLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [accountLoading, setAccountLoading] = useState(true);

  const [journals, setJournals] = useState([]);
  const [journalLoading, setJournalLoading] = useState(true);

  const cardBg = useColorModeValue("white", "gray.700");

  const { clocks, loading: clocksLoading } = useClocks();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch("https://poc.mrc.ai/api/products");
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchProductsContracts = async () => {
      try {
        const response = await fetch(
          "https://poc.mrc.ai/api/products/contracts"
        );
        const data = await response.json();
        setProductsContracts(data);
      } catch (err) {
        console.error("Failed to fetch products contracts:", err);
      } finally {
        setProductsContractsLoading(false);
      }
    };

    const fetchParties = async () => {
      try {
        const res = await fetch("https://directory.poc.mrc.ai/api/parties");
        const data = await res.json();
        setParties(data);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
      } finally {
        setPartyLoading(false);
      }
    };

    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
        console.error("Failed to fetch registers:", err);
      } finally {
        setRegisterLoading(false);
      }
    };

    const fetchDocumentDefinitions = async () => {
      try {
        const res = await fetch("https://documents.poc.mrc.ai/api/definitions");
        const data = await res.json();
        setDocumentDefinitions(data);
      } catch (err) {
        console.error("Failed to fetch document definitions:", err);
      } finally {
        setDocumentDefinitionsLoading(false);
      }
    };

    const fetchCodifyDefinitions = async () => {
      try {
        const res = await fetch("https://codify.poc.mrc.ai/api/definitions");
        const data = await res.json();
        setCodifyDefinitions(data);
      } catch (err) {
        console.error("Failed to fetch codify definitions:", err);
      } finally {
        setCodifyDefinitionsLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await fetch("https://auth.poc.mrc.ai/api/users");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        setUserLoading(false);
      }
    };

    const fetchSmartLedgers = async () => {
      try {
        const res = await fetch("https://ledgers.poc.mrc.ai/api/smartledgers");
        const data = await res.json();
        setSmartLedgers(data);
      } catch (err) {
        console.error("Failed to fetch Smart Ledgers:", err);
      } finally {
        setSmartLedgerLoading(false);
      }
    };

    const fetchAccounts = async () => {
      try {
        const res = await fetch("https://ledgers.poc.mrc.ai/api/accounts");
        const data = await res.json();
        setAccounts(data);
      } catch (err) {
        console.error("Failed to fetch Accounts:", err);
      } finally {
        setAccountLoading(false);
      }
    };

    const fetchJournals = async () => {
      try {
        const res = await fetch("https://ledgers.poc.mrc.ai/api/journals");
        const data = await res.json();
        setJournals(data);
      } catch (err) {
        console.error("Failed to fetch Journals:", err);
      } finally {
        setJournalLoading(false);
      }
    };

    fetchProducts();
    fetchProductsContracts();
    fetchParties();
    fetchRegisters();
    fetchDocumentDefinitions();
    fetchCodifyDefinitions();
    fetchUsers();
    fetchSmartLedgers();
    fetchAccounts();
    fetchJournals();
  }, []);

  const handleDeleteTopic = async (topic) => {
    try {
      const res = await fetch(
        `https://pipes.mrc.ai/api/topics/${encodeURIComponent(topic)}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error(`Failed to delete ${topic}`);
      setTopicsTable((prev) =>
        prev
          .map((group) => ({
            ...group,
            items: group.items.filter((t) => t !== topic),
          }))
          .filter((group) => group.items.length > 0)
      );
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  };

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <Navbar />
      </Box>

      {/* Watermark background */}
      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={1} display="flex" flexDirection="column" height="100%">
          <Tabs
            orientation="horizontal"
            variant="line"
            colorScheme="linkedin"
            isLazy={true}
            lazyBehavior="unmount"
            display="flex"
            flexDirection="column"
            height="100%">
            <TabList>
              <Tab>Dashboard</Tab>
              {isUserSuperAdmin && <Tab>Products</Tab>}
              {isUserSuperAdmin && <Tab>Parties</Tab>}
              {isUserSuperAdmin && <Tab>Registers</Tab>}
              {isUserSuperAdmin && <Tab>Document Definitions</Tab>}
              {isUserSuperAdmin && <Tab>Codify Definitions</Tab>}
              {isUserSuperAdmin && <Tab>Pipelines</Tab>}
              {isUserSuperAdmin && <Tab>Services</Tab>}
              {isUserSuperAdmin && <Tab>Server Files</Tab>}
              {isUserSuperAdmin && <Tab>Smart Ledgers</Tab>}
              {isUserSuperAdmin && <Tab>Users</Tab>}
            </TabList>
            <TabPanels flex="1" overflow="auto">
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Platform Clocks */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  mb={2}
                  flexShrink={0}>
                  {clocksLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {clocks.map((clock) => (
                        <ClockCard key={clock._id} clock={clock} />
                      ))}
                    </Wrap>
                  )}
                </Box>
                {/* Dashboard Panel */}
                <Box flex="1" minHeight={0}>
                  <Grid
                    templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                    templateRows={{ base: "auto", lg: "1fr 1fr" }}
                    gap={2}
                    width="100%"
                    height="100%">

                    {/* My Workflow box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <Heading size="sm" mb={2}>
                        My Workflow
                      </Heading>
                      <Box flex="1" overflow="auto">
                        <MyWorkflowList />
                      </Box>
                    </Box>

                    {/* My Contracts box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      gridRow={{ base: "auto", lg: "1 / span 2" }}
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <Heading size="sm" mb={2}>
                        My Contracts
                      </Heading>
                      <Box flex="1" overflow="auto">
                        <Table
                          variant="unstyled"
                          size="sm"
                          fontSize="xs"
                          style={{ tableLayout: "auto", width: "100%" }}
                          sx={{
                            "tbody tr:nth-of-type(odd)": {
                              backgroundColor: "gray.200",
                            },
                          }}>
                          <Thead position="sticky" top={0} bg="white" zIndex={1}>
                            <Tr>
                              <Th fontSize="xs">Product</Th>
                              <Th fontSize="xs">Product Name</Th>
                              <Th fontSize="xs">Contract Name</Th>
                              <Th fontSize="xs">Placing Stage</Th>
                              <Th fontSize="xs">Transaction Function</Th>
                              <Th fontSize="xs"></Th>
                            </Tr>
                          </Thead>

                          <Tbody>
                            {productsContracts.map((productcontract) => (
                              <Tr key={productcontract.id}>
                                <Td fontSize="xs">{productcontract.code}</Td>
                                <Td fontSize="xs">{productcontract.name}</Td>
                                <Td fontSize="xs">
                                  {productcontract.contractName}
                                </Td>
                                <Td fontSize="xs">
                                  {productcontract.contractPlacingStageCode}
                                </Td>
                                <Td fontSize="xs">
                                  {
                                    productcontract.contractPlacingTransactionFunctionCode
                                  }
                                </Td>
                                <Td fontSize="xs">
                                  <Tooltip
                                    label="Edit Contract"
                                    aria-label="Edit Contract Tooltip">
                                    <IconButton
                                      icon={<HiOutlineMenu />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/contract`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>
                                  <Tooltip
                                    label="Edit Placement"
                                    aria-label="Edit Placement Tooltip">
                                    <IconButton
                                      icon={<HiOutlineCode />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/placement`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>

                                  <Tooltip
                                    label="Create Standard Signal"
                                    aria-label="Create Standard Signal Tooltip">
                                    <IconButton
                                      icon={<HiOutlineStatusOnline />}
                                      size="md"
                                      onClick={() =>
                                        navigate(
                                          `/product/${productcontract.code}/contract/${productcontract.contractId}/signal`
                                        )
                                      }
                                      _hover={{ transform: "scale(1.1)" }}
                                      transition="transform 0.2s ease"
                                    />
                                  </Tooltip>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>

                    {/* My Registers box */}
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor="gray.500"
                      backgroundColor="whiteAlpha.800"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden">
                      <Heading size="sm" mb={2}>
                        My Registers
                      </Heading>
                      <Box flex="1" overflow="auto">
                        <Box>
                          <Box
                            borderWidth="1px"
                            borderRadius="sm"
                            mb={1}
                            borderColor={"gray.500"}
                            backgroundColor="whiteAlpha.800">
                            <HStack>
                              <Tooltip label="Add register">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  aria-label="Add register"
                                  size="sm"
                                  onClick={() => navigate("/registers/new")}
                                />
                              </Tooltip>
                            </HStack>
                          </Box>

                          {registerLoading ? (
                            <Spinner />
                          ) : (
                            <Wrap spacing={4}>
                              {registers.map((register) => (
                                <WrapItem key={register._id}>
                                  <Box
                                    bg={cardBg}
                                    p={4}
                                    rounded="lg"
                                    shadow="md"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    minW="200px"
                                    maxW="fit-content"
                                    transition="all 0.2s ease"
                                    _hover={{
                                      shadow: "lg",
                                      transform: "translateY(-4px)",
                                    }}>
                                    <HStack>
                                      <Tooltip label="Edit Register...">
                                        <IconButton
                                          icon={<HiEye />}
                                          aria-label="Edit register"
                                          size="sm"
                                          onClick={() =>
                                            navigate(
                                              `/registers/${register._id}`
                                            )
                                          }
                                        />
                                      </Tooltip>
                                      <Heading size="sm" mb={0} noOfLines={1}>
                                        {register.name}
                                      </Heading>
                                    </HStack>
                                    {register.entries.map((entry) => (
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        mt={2}
                                        key={register._id}>
                                        {entry.name}
                                      </Text>
                                    ))}
                                  </Box>
                                </WrapItem>
                              ))}
                            </Wrap>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Products Panel */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {loading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {products.map((product) => (
                        <WrapItem key={product._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            minW="250px"
                            maxW="320px"
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack
                              align="center"
                              justify="flex-start"
                              mb={2}
                              spacing={1}>
                              <Tooltip label="Go to the Product Dashboard">
                                <IconButton
                                  icon={<HiHome />}
                                  size="md"
                                  onClick={() =>
                                    navigate(`/product/${product.code}`)
                                  }
                                  aria-label="Go to product"
                                />
                              </Tooltip>
                              <Heading
                                size="sm"
                                textAlign="left"
                                mb={0}
                                noOfLines={1}>
                                {product.name}
                              </Heading>
                              <Text as="span" fontSize="sm" color="gray.500">
                                ({product.code})
                              </Text>
                            </HStack>
                            <Text
                              fontSize="xs"
                              color="gray.700"
                              mb={1}
                              noOfLines={1}>
                              {product.description}
                            </Text>

                            <SimpleGrid>
                              <Box
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200">
                                <Tooltip label="Edit Product Definition">
                                  <IconButton
                                    icon={<HiOutlineCog />}
                                    size="md"
                                    onClick={() =>
                                      navigate(
                                        `/poc/product/${product.code}/edit`,
                                        {
                                          state: { isNew: false }, // ✅ mark it as not new
                                        }
                                      )
                                    }
                                    aria-label="Go to product"
                                  />
                                </Tooltip>
                              </Box>
                            </SimpleGrid>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Directory */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add party">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add party"
                          size="md"
                          onClick={() => navigate("/party/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {partyLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {parties.map((party) => (
                        <WrapItem key={party._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit party details">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit party"
                                  size="sm"
                                  onClick={() => handleOpenParty(party)}
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {party.name}
                              </Heading>
                            </HStack>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              {party.roleCode}
                            </Text>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Registers */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add register">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add register"
                          size="md"
                          onClick={() => navigate("/registers/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {registerLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {registers.map((register) => (
                        <WrapItem key={register._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit Register...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit register"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/registers/${register._id}`)
                                  }
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {register.name}
                              </Heading>
                            </HStack>
                            {register.entries.map((entry) => (
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                mt={2}
                                key={register._id}>
                                {entry.name}
                              </Text>
                            ))}
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Document Definitions */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Document Definition">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Document Definition"
                        size="md"
                        onClick={() => navigate("/documentdefinitions/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {documentDefinitionsLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {documentDefinitions.map((documentDefinition) => (
                      <WrapItem key={documentDefinition._id}>
                        <Box
                          bg={cardBg}
                          p={4}
                          rounded="lg"
                          shadow="md"
                          border="1px solid"
                          borderColor="gray.300"
                          minW="200px"
                          maxW="fit-content"
                          transition="all 0.2s ease"
                          _hover={{
                            shadow: "lg",
                            transform: "translateY(-4px)",
                          }}>
                          <HStack>
                            <Tooltip label="Edit Document Definition...">
                              <IconButton
                                icon={<HiEye />}
                                aria-label="Edit document definition"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/documentdefinitions/${documentDefinition._id}`
                                  )
                                }
                              />
                            </Tooltip>
                            <Heading size="sm" mb={0} noOfLines={1}>
                              {documentDefinition.name}
                            </Heading>
                          </HStack>

                          {documentDefinition.segments.map((segment) => (
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              mt={2}
                              key={segment.name}>
                              {segment.name}
                            </Text>
                          ))}
                        </Box>
                      </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Codify Definitions */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Codify Definition">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Codify Definition"
                        size="md"
                        onClick={() => navigate("/codifydefinitions/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden">
                  <CodifyDefinitionsGrid
                    codifyDefinitions={codifyDefinitions}
                    loading={codifyDefinitionsLoading}
                    onCopy={handleCodifyDefinitionCopy}
                  />
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Pipeline */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Button
                    colorScheme="linkedin"
                    onClick={async () => {
                      try {
                        const response = await fetch(
                          "https://pipes.mrc.ai/api/topics"
                        );
                        if (!response.ok)
                          throw new Error("Failed to fetch topics");

                        const data = await response.json(); // array of topic strings

                        const group = (label, match) => {
                          const items = data
                            .filter((topic) => topic.includes(`.${match}.`))
                            .sort();
                          return items.length > 0 ? { label, items } : null;
                        };

                        const others = data
                          .filter(
                            (topic) =>
                              !topic.includes(".signals.") &&
                              !topic.includes(".triggers.") &&
                              !topic.includes(".events.") &&
                              !topic.includes(".temporal.")
                          )
                          .sort();

                        const groups = [
                          group("SIGNALS", "signals"),
                          group("TRIGGERS", "triggers"),
                          group("EVENTS", "events"),
                          group("TEMPORAL", "temporal"),
                          others.length > 0
                            ? { label: "OTHER", items: others }
                            : null,
                        ].filter(Boolean); // remove nulls

                        setTopicsTable(groups);
                      } catch (err) {
                        setTopicsTable([
                          { label: "ERROR", items: [`❌ ${err.message}`] },
                        ]);
                      }
                    }}>
                    View Channels
                  </Button>

                  {topicsTable.length === 0 ? (
                    <Text fontSize="sm" color="gray.700" mt={4}>
                      No channels loaded
                    </Text>
                  ) : (
                    <Table
                      size="sm"
                      mt={4}
                      variant="striped"
                      colorScheme="gray">
                      <Thead>
                        <Tr>
                          <Th>Group</Th>
                          <Th>Channel</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {topicsTable.map((group, groupIdx) =>
                          group.items.map((topic, topicIdx) => (
                            <Tr key={`${group.label}-${topicIdx}`}>
                              <Td>{topicIdx === 0 ? group.label : ""}</Td>
                              <Td>{topic}</Td>
                              <Td>
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  aria-label="Delete topic"
                                  size="xs"
                                  onClick={() => handleDeleteTopic(topic)}
                                />
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Services */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <ServicePanel />
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Server Files */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <FileEditorView />
                </Box>
              </TabPanel>

              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                {/* Smart Ledgers */}
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Tabs
                    orientation="horizontal"
                    variant="line"
                    colorScheme="linkedin">
                    <TabList>
                      <Tab>Smart Ledgers</Tab>
                      <Tab>Accounts</Tab>
                      <Tab>Journals</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          mb={2}
                          borderColor={"gray.500"}
                          backgroundColor="whiteAlpha.800">
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            mb={2}
                            borderColor={"gray.500"}
                            backgroundColor="whiteAlpha.800">
                            <HStack>
                              <Tooltip label="Add Smart Ledger">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  aria-label="Add Smart Ledger"
                                  size="md"
                                  onClick={() => navigate("/smartledgers/new")}
                                />
                              </Tooltip>
                            </HStack>
                          </Box>
                        </Box>

                        {smartLedgerLoading ? (
                          <Spinner />
                        ) : (
                          <Wrap spacing={4}>
                            {smartLedgers.map((smartLedger) => (
                              <WrapItem key={smartLedger._id}>
                                <Box
                                  bg={cardBg}
                                  p={4}
                                  rounded="lg"
                                  shadow="md"
                                  border="1px solid"
                                  borderColor="gray.300"
                                  minW="200px"
                                  maxW="fit-content"
                                  transition="all 0.2s ease"
                                  _hover={{
                                    shadow: "lg",
                                    transform: "translateY(-4px)",
                                  }}>
                                  <HStack>
                                    <Tooltip label="Edit Smart Ledger...">
                                      <IconButton
                                        icon={<HiOutlineLibrary />}
                                        aria-label="Open Smart Ledger"
                                        size="sm"
                                        onClick={() =>
                                          navigate(
                                            `/smartledgers/${smartLedger._id}`
                                          )
                                        }
                                      />
                                    </Tooltip>
                                    <Heading size="sm" mb={0} noOfLines={1}>
                                      {smartLedger.name}
                                    </Heading>
                                  </HStack>
                                  {smartLedger.accounts?.map((acc) => (
                                    //show acc.name as FormLabel
                                    <FormControl key={acc._id} mt={2}>
                                      <FormLabel>
                                        <AccountNameText
                                          size="xs"
                                          accountId={acc.id}
                                        />
                                      </FormLabel>
                                    </FormControl>
                                  ))}
                                </Box>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </TabPanel>
                      <TabPanel>
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          mb={2}
                          borderColor={"gray.500"}
                          backgroundColor="whiteAlpha.800">
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            mb={2}
                            borderColor={"gray.500"}
                            backgroundColor="whiteAlpha.800">
                            <HStack>
                              <Tooltip label="Add Account">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  aria-label="Add Account"
                                  size="md"
                                  onClick={() => navigate("/accounts/new")}
                                />
                              </Tooltip>
                            </HStack>
                          </Box>
                        </Box>

                        {accountLoading ? (
                          <Spinner />
                        ) : (
                          <Wrap spacing={4}>
                            {accounts.map((account) => (
                              <WrapItem key={account._id}>
                                <Box
                                  bg={cardBg}
                                  p={4}
                                  rounded="lg"
                                  shadow="md"
                                  border="1px solid"
                                  borderColor="gray.300"
                                  minW="200px"
                                  maxW="fit-content"
                                  transition="all 0.2s ease"
                                  _hover={{
                                    shadow: "lg",
                                    transform: "translateY(-4px)",
                                  }}>
                                  <HStack>
                                    <Tooltip label="Edit Account...">
                                      <IconButton
                                        icon={<HiOutlineBookOpen />}
                                        aria-label="Open Account"
                                        size="xs"
                                        onClick={() =>
                                          navigate(`/accounts/${account._id}`)
                                        }
                                      />
                                    </Tooltip>
                                    <Heading size="sm" mb={0} noOfLines={1}>
                                      {account.name}
                                    </Heading>
                                  </HStack>
                                </Box>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </TabPanel>

                      <TabPanel>
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          p={1}
                          mb={2}
                          borderColor={"gray.500"}
                          backgroundColor="whiteAlpha.800">
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            mb={2}
                            borderColor={"gray.500"}
                            backgroundColor="whiteAlpha.800">
                            <HStack>
                              <Tooltip label="Add Journal">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  aria-label="Add Journal"
                                  size="md"
                                  onClick={() => navigate("/journals/new")}
                                />
                              </Tooltip>
                            </HStack>
                          </Box>
                        </Box>

                        {journalLoading ? (
                          <Spinner />
                        ) : (
                          <Wrap spacing={4}>
                            {journals.map((journal) => (
                              <WrapItem key={journal._id}>
                                <Box
                                  bg={cardBg}
                                  p={4}
                                  rounded="lg"
                                  shadow="md"
                                  border="1px solid"
                                  borderColor="gray.300"
                                  minW="200px"
                                  maxW="fit-content"
                                  transition="all 0.2s ease"
                                  _hover={{
                                    shadow: "lg",
                                    transform: "translateY(-4px)",
                                  }}>
                                  <HStack>
                                    <Tooltip label="Edit Journal...">
                                      <IconButton
                                        icon={<HiOutlineMenu />}
                                        aria-label="Open Journal"
                                        size="sm"
                                        onClick={() =>
                                          navigate(`/journals/${journal._id}`)
                                        }
                                      />
                                    </Tooltip>
                                    <Heading size="xs" mb={0} noOfLines={1}>
                                      {journal.name}
                                    </Heading>
                                  </HStack>
                                  {Array.isArray(journal.journalLines) &&
                                    journal.journalLines.map((journalline) => (
                                      <Text
                                        fontSize="sm"
                                        color="gray.500"
                                        mt={2}
                                        key={journalline._id || journalline}>
                                        {typeof journalline === "object" &&
                                        journalline.name
                                          ? journalline.name
                                          : typeof journalline === "string"
                                          ? journalline
                                          : ""}
                                      </Text>
                                    ))}
                                </Box>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Box>
              </TabPanel>
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add user">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add user"
                          size="md"
                          onClick={() => navigate("/users/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {userLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {users.map((user) => (
                        <WrapItem key={user._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit User...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Open user"
                                  size="sm"
                                  onClick={() => navigate(`/users/${user._id}`)}
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {user.firstName} {user.lastName}
                              </Heading>
                            </HStack>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              {user.email}
                            </Text>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default Poc;
